import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export const Manifesto = () => {
  const { t } = useTranslation();
  const chapters = t("manifesto.chapters", { returnObjects: true });

  return (
    <section id="product" data-testid="manifesto-section" className="py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-xs font-medium uppercase tracking-[0.25em] text-mint mb-4"
        >
          {t("manifesto.kicker")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="font-display font-semibold tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl max-w-2xl text-balance"
        >
          {t("manifesto.title")}
        </motion.h2>

        <div className="mt-20 grid lg:grid-cols-3 gap-6">
          {chapters.map((c, i) => (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.15, ease: EASE }}
              data-testid={`manifesto-chapter-${c.num}`}
              className="group relative rounded-3xl border border-white/8 bg-card p-8 lg:p-10 transition-colors duration-300 hover:border-mint/30"
            >
              <span className="font-display text-6xl lg:text-7xl font-semibold text-white/8 group-hover:text-mint/20 transition-colors duration-300">
                {c.num}
              </span>
              <h3 className="mt-8 font-display font-semibold text-xl lg:text-2xl text-white tracking-tight">
                {c.title}
              </h3>
              <p className="mt-4 text-sm lg:text-base text-white/55 leading-relaxed">{c.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          data-testid="manifesto-solution"
          className="mt-6 relative overflow-hidden rounded-3xl border border-mint/25 bg-gradient-to-br from-mint/10 via-card to-card p-10 lg:p-16"
        >
          <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-mint/15 blur-[100px] pointer-events-none" />
          <p className="text-xs font-medium uppercase tracking-[0.25em] text-mint mb-4">{t("manifesto.solution.kicker")}</p>
          <h3 className="font-display font-semibold tracking-tighter text-white text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
            {t("manifesto.solution.title")}
          </h3>
          <p className="mt-5 text-base lg:text-lg text-white/65 max-w-2xl leading-relaxed">{t("manifesto.solution.text")}</p>
          <a
            href="#waitlist"
            data-testid="manifesto-solution-cta"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-mint px-6 py-3 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105"
          >
            {t("manifesto.solution.cta")}
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={2} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
